import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Linkedin, Twitter, Youtube, Instagram, Mail } from 'lucide-react';
import { Separator } from './ui/separator';

const Footer = ({ isDarkMode }) => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Twitter, href: '#', label: 'Twitter' },
    { icon: Youtube, href: '#', label: 'YouTube' },
    { icon: Instagram, href: '#', label: 'Instagram' },
  ];

  const footerSections = [
    {
      title: 'Explore',
      links: [
        { label: 'Home', href: '/', onClick: () => navigate('/') },
        { label: 'Bookmarks', href: '/bookmarks', onClick: () => navigate('/bookmarks') },
        { label: 'About Us', href: '/about', onClick: () => navigate('/about') },
      ],
    },
    {
      title: 'Resources',
      links: [
        { label: 'Learning Guides', href: '#' },
        { label: 'FAQ', href: '#' },
        { label: 'Support', href: '#' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Privacy Policy', href: '/privacy', onClick: () => navigate('/privacy') },
        { label: 'Terms of Service', href: '/terms', onClick: () => navigate('/terms') },
        { label: 'Cookie Policy', href: '/cookies', onClick: () => navigate('/cookies') },
      ],
    },
  ];

  return (
    <footer className={`${isDarkMode ? 'bg-velvet' : 'bg-sapphire'} text-cream-light transition-colors duration-300`}>
      {/* Accent Line */}
      <div className="h-1 bg-gradient-to-r from-gold via-gold-light to-gold" />

      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1 animate-fadeInUp">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-gold to-gold-light rounded-lg flex items-center justify-center">
                <BookOpen className="h-6 w-6 text-velvet" />
              </div>
              <span className="font-serif font-bold text-xl">EduLearn</span>
            </div>
            <p className="text-cream-light/70 text-sm leading-relaxed mb-4">
              Premium education crafted for the intellectually curious. Elevate your future with world-class learning.
            </p>
            {/* Newsletter Signup */}
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Enter email"
                className="flex-1 px-4 py-2 rounded-lg bg-sapphire-light/30 border border-gold/30 text-cream-light placeholder-cream-light/50 focus:outline-none focus:border-gold transition-colors text-sm"
              />
              <button className="px-4 py-2 bg-gold hover:bg-gold-light text-velvet rounded-lg font-semibold transition-colors">
                <Mail className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Footer Sections */}
          {footerSections.map((section, idx) => (
            <div
              key={section.title}
              className="animate-fadeInUp"
              style={{ animationDelay: `${(idx + 1) * 0.1}s` }}
            >
              <h3 className="font-serif font-semibold text-lg mb-4 text-gold">{section.title}</h3>
              <ul className="space-y-2">
                {section.links.map((link) => (
                  <li key={link.label}>
                    <a
                      href={link.href}
                      onClick={(e) => {
                        if (link.onClick) {
                          e.preventDefault();
                          link.onClick();
                        }
                      }}
                      className="text-cream-light/70 hover:text-gold transition-colors duration-300 text-sm cursor-pointer"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <Separator className="bg-sapphire-light/30 mb-8" />

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Copyright */}
          <p className="text-cream-light/60 text-sm">
            © {currentYear} EduLearn. All rights reserved. Crafted with excellence.
          </p>

          {/* Social Links */}
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.href}
                  aria-label={social.label}
                  className="w-10 h-10 rounded-full border border-gold/30 flex items-center justify-center text-cream-light hover:bg-gold hover:text-velvet hover:border-gold transition-all duration-300 group"
                >
                  <Icon className="h-4 w-4 group-hover:scale-110 transition-transform" />
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
